import React from "react";
import PageHero from "../components/PageHero";
import ContactForm from "../components/ContactForm";
import { Star, Quote } from "lucide-react";
import { testimonials, company } from "../mock";
import JsonLd from "../components/JsonLd";
import useSEO from "../hooks/useSEO";
import { BIZ_ID } from "../seo";

const extra = [
  { quote: "The whole crew was respectful and cleaned up every day. Our new roof looks fantastic and the price was fair.", name: "Priya K.", location: "Reston, VA", rating: 5 },
  { quote: "They replaced our siding and gutters after a storm. Communication was excellent from quote to completion.", name: "James L.", location: "Alexandria, VA", rating: 5 },
  { quote: "Honest, professional and fast. They found damage two other companies missed. Couldn't be happier.", name: "Tanya R.", location: "Silver Spring, MD", rating: 5 },
];

const Reviews = () => {
  useSEO({
    title: "Fab Build Pro Reviews | 4.9-Star Roofer in Vienna, VA",
    description: "Read Fab Build Pro reviews from homeowners across Northern Virginia, D.C. & Maryland. Rated 4.9 stars on Google for roofing, siding & storm repair.",
    crumbs: [{ name: "Reviews", path: "/reviews" }],
  });
  const all = [...testimonials, ...extra];
  const reviewSchema = {
    "@context": "https://schema.org",
    "@type": "RoofingContractor",
    "@id": BIZ_ID,
    name: "Fab Build Pro",
    telephone: "+1-703-766-6599",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: String(company.rating),
      reviewCount: String(company.reviewCount),
    },
    review: all.map((t) => ({
      "@type": "Review",
      author: { "@type": "Person", name: t.name },
      reviewRating: { "@type": "Rating", ratingValue: String(t.rating), bestRating: "5" },
      reviewBody: t.quote,
    })),
  };
  return (
    <>
      <JsonLd data={reviewSchema} id="reviews-schema" />
      <PageHero
        title="Customer Reviews"
        subtitle={`Rated ${company.rating}★ by homeowners across the DMV on Google.`}
      />

      <section className="bg-brand-cream py-16">
        <div className="max-w-4xl mx-auto px-6 text-center mb-12">
          <a
            href={company.googleReviewsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white rounded-full px-6 py-3 shadow-sm border border-black/5 hover:shadow-md transition-shadow"
          >
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 fill-brand-orange text-brand-orange" />
              ))}
            </div>
            <span className="font-display font-bold text-brand-navy text-lg">
              {company.rating} · Read our Google Reviews
            </span>
          </a>
        </div>

        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {all.map((t, i) => (
            <div key={i} className="bg-white rounded-2xl p-7 border border-black/5 shadow-sm hover:shadow-lg transition-shadow relative">
              <Quote className="w-10 h-10 text-brand-orange/15 absolute top-5 right-5" />
              <div className="flex gap-1 mb-4">
                {[...Array(t.rating)].map((_, k) => (
                  <Star key={k} className="w-5 h-5 fill-brand-orange text-brand-orange" />
                ))}
              </div>
              <p className="text-slate-700 leading-relaxed mb-6">"{t.quote}"</p>
              <div className="flex items-center gap-3 pt-4 border-t border-black/5">
                <div className="w-11 h-11 rounded-full bg-brand-navy text-white font-display font-bold flex items-center justify-center">
                  {t.name.charAt(0)}
                </div>
                <div>
                  <p className="font-semibold text-brand-navy">{t.name}</p>
                  <p className="text-sm text-slate-500">{t.location}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <ContactForm />
    </>
  );
};

export default Reviews;
